// stateReducer.js

// NOTE: Check this out for determining 'type':  https://redfern.dev/articles/javascript-typeof-number/
// Object.prototype.toString.call(77); // '[object Number]'
// Object.prototype.toString.call("hi"); // '[object String]'
// Object.prototype.toString.call(["hi"]); // '[object Array]'
// Object.prototype.toString.call({ greet: "hi" }); // '[object Object]'
// Object.prototype.toString.call(new Date()); // '[object Date]'         <-- Also worked with 'Date'!

import objectHash from "object-hash";

const stateReducer = (previous,obj) => {
  // Recursive on obj=array to produce single state update of multiple attributes.
  if (Array.isArray(obj)) {
    const newState = Object.assign({}, previous);
    obj.forEach((_obj)=>{
      Object.assign(newState, stateReducer(newState,_obj));
    });
    return newState;
  }
  // Having obj.field string value indicates a nested-data update, not the entire data object.
  // Only the nested data array-item matching the unique id of [obj.field] will be updated.
  // Example: obj: {key:"schedulesData",field:item_id,type:"object",data:{some_obj_exected_in_data_array_with_field_matching_obj.field}}

  // Eject for any of these invalid states:
  if (_typeof(obj)!=="object" || _typeof(obj.key)!=="string" || !obj.key) {
    const errStr = "Unexpected or missing required data paramater object.";
    console.log(errStr, "previous, obj =", previous, obj);
    throw new Error(errStr);
  }
  if (_typeof(obj.type)!=="string" || !obj.type) {
    const errStr = "Missing property 'type'.";
    console.log(errStr, "previous, obj =", previous, obj);
    throw new Error(errStr);
  }
  if (!previous.hasOwnProperty(obj.key)) {
    const errStr = `Missing expected property in Source Data object. Expected '${obj.key}'.`;
    console.log(errStr, "previous, obj =", previous, obj);
    throw new Error(errStr);
  }
  if (!!obj.hasOwnProperty("field") && (Array.isArray(previous[obj.key]) && !previous[obj.key][0].hasOwnProperty(obj.field))) {
    const errStr = `Missing expected property '${obj.field}' set in 'field' of source type=array.`;
    console.log(errStr, "previous, obj =", previous, obj);
    throw new Error(errStr);
  }
  if (!!obj.hasOwnProperty("field") && (!Array.isArray(previous[obj.key]) && _typeof(previous[obj.key])==="object" && !previous[obj.key].hasOwnProperty(obj.field))) {
    const errStr = `Missing expected property '${obj.field}' set in 'field' of source type=object.`;
    console.log(errStr, "previous, obj =", previous, obj);
    throw new Error(errStr);
  }
  if (_typeof(obj[obj.key])!==obj.type && _typeof(obj[obj.key])!=="null" && !obj.field) {
    const errStr = `Missing expected property in Source Data object. Expected '${obj.key}' of type '${obj.type}' but got '${_typeof(obj[obj.key])}'. Or expected property 'field' for matching data type.`;
    console.log(errStr, "previous, obj =", previous, obj);
    throw new Error(errStr);
  }
  if (obj.type==="array" && !Array.isArray(obj[obj.key])) {
    const errStr = `Unexpected datatype of property '${obj.key}'. Expected type '${obj.type}'.`;
    console.log(errStr, "previous, obj =", previous, obj);
    throw new Error(errStr);
  }

  // Valid Object - Continue...
  // Case 1: ARRAY -> [FIELD]
  // Property is type 'Array' -> find index of array item on matching [field].
  if (Array.isArray(previous[obj.key]) && _typeof(obj.field)==="string" && !!obj.field && _typeof(previous[obj.key][0])===obj.type && previous[obj.key][0]?.hasOwnProperty(obj.field) && obj[obj.key]?.hasOwnProperty(obj.field)) {
    // This upates second-level data - a row in the data array.
    const newState = Object.assign({}, previous);
    const targetIndex = newState[obj.key].findIndex(_o=>(_o[obj.field]===obj[obj.key][obj.field]));
    // This should never happen, just in case, prevent improper state overwrite.
    if ((_typeof(newState[obj.key][targetIndex])!==obj.type && _typeof(newState[obj.key][targetIndex])!=="null") || targetIndex===-1) {
      const errStr = `Unexpected data type of data array item type'${_typeof(newState[obj.key][targetIndex])}'. Expected type '${obj.type}'.`;
      console.log("Error:", errStr, "previous, obj =", previous, obj);
      throw new Error(errStr);
    }
    newState[obj.key][targetIndex] = obj[obj.key];
    return newState;
    // Success!
  }
  // Case 2: OBJECT -> [FIELD]
  // Property is type 'object' -> validate object key named [field].
  if (!Array.isArray(previous[obj.key]) && (_typeof(previous[obj.key]==="object")||_typeof(previous[obj.key]==="null")) && _typeof(obj.field)==="string" && !!obj.field && (_typeof(obj.fieldtype)==="string"||_typeof(obj.fieldtype)==="null") && !!obj.fieldtype
    && !!previous[obj.key].hasOwnProperty(obj.field) && ((obj.fieldtype==="array" && Array.isArray(previous[obj.key][obj.field])) || _typeof(previous[obj.key][obj.field])===obj.fieldtype || _typeof(previous[obj.key][obj.field])==="null") && !!obj.hasOwnProperty(obj.field)
  ) {
    // This upates second-level data - a row in the data array.
    const newState = Object.assign({}, previous);
    newState[obj.key][obj.field] = obj[obj.field];
    return newState;
    // Success!
  }
  // Case 3: Full "Object"
  if (!obj.hasOwnProperty("field") && (_typeof(previous[obj.key])===obj.type || _typeof(previous[obj.key])==="null")  &&  (obj.type!=="array" || Array.isArray(previous[obj.key]))) {
    // If no 'field' value to match, update the entire target property:
    // This upates first-level data.
    // Example: obj: {key:"isScreenLoading",type:"boolean",isScreenLoading:true}
    // Example: obj: {key:"ganttViewMode",type:"boolean",ganttViewMode:"Week"}
    // Example: obj: [{key:"isScreenLoading",type:"boolean",isScreenLoading:true},{key:"ganttViewMode",type:"boolean",ganttViewMode:"Week"}]
    // Example: obj: {key:"schedulesData", type:"array", schedulesData:{new_data_array}}
    const newState = Object.assign({}, previous);
    newState[obj.key] = obj[obj.key];
    return newState;
    // Success!
  }
  throw new Error(`Unexpected validation error. Please try again. [${obj.key}]`);
};

const deepCopy = (obj) => {
  if (_typeof(obj)!=="object") {
    return obj;
  }
  return structuredClone(obj);
};

const _typeof = (v)=>{ return Object.prototype.toString.call(v).match(/\[object (\w+)\]/)[1].toLowerCase(); };  // All possible results: ["boolean","string","number","object","array","date","function","undefined","null"].
// const _typeof = (v)=>{ return typeof(v); };

export {
  stateReducer,
  deepCopy,
  objectHash,
  _typeof,
};